import React from "react";
import {
  Badge,
  Table,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from "flowbite-react";
import { ReceiptEuro } from "lucide-react";
import {
  Expense, SortableTableHead, ExpenseDetailsPanel, ExpensesListSkeleton,
} from "@/features/vouchers";
import { ExpenseDefaultLogo } from "@/features/vouchers/components/ExpenseDefaultLogo";
import { useExpenseDetails } from "@/features/vouchers/hooks/useExpenseDetails";
import { useExpenseSort } from "@/features/vouchers/hooks/useExpenseSort";
import { formatDate } from "@/shared/utils/format.util";
import { formatEuro } from "@/shared/utils/formatEuro";

type VouchersAndExpensesListProps = {
  expenses: Expense[]
  isLoadingExpenses: boolean
};

const shouldEnableExpansion = (_expense: Expense, _index: number) => {
  return true;
};

const EmptyExpensesState = () => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <ReceiptEuro className="text-error mb-4" size={56} />
    <p className="text-foreground">
      <span className="font-bold">Vous n'avez aucune dépense liée à ce bon.</span>
      <br />
      <span className="text-sm">
        Vous pouvez utiliser ce bon pour réaliser des dépenses dans les enseignes éligibles avec votre carte
        <span className="font-bold">YEATI</span>
        .
      </span>
    </p>
  </div>
);

export const VouchersAndExpensesList = ({ expenses, isLoadingExpenses }: VouchersAndExpensesListProps) => {
  const {
    sortedExpenses, sortState, handleSort, getSortIcon,
  } = useExpenseSort({ expenses });
  const { toggleExpenseDetails, isExpanded } = useExpenseDetails();

  const handleRowClick = (expense: Expense, index: number) => {
    if (shouldEnableExpansion(expense, index)) {
      toggleExpenseDetails(expense);
    }
  };

  return (
    <div className="overflow-x-auto">
      {isLoadingExpenses
        ? (
          <ExpensesListSkeleton />
        )
        : expenses.length === 0
          ? (
            <EmptyExpensesState />
          )
          : (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
              <Table hoverable className="w-full">
                <TableHead className="uppercase">
                  <TableRow>
                    <SortableTableHead
                      column="date"
                      label="Date"
                      sortIcon={getSortIcon("date")}
                      onSort={handleSort}
                      isActive={sortState.column === "date"}
                    />
                    <SortableTableHead
                      column="merchant"
                      label="Marchand"
                      sortIcon={getSortIcon("merchant")}
                      onSort={handleSort}
                      isActive={sortState.column === "merchant"}
                    />
                    <SortableTableHead
                      column="amount"
                      label="Montant"
                      sortIcon={getSortIcon("amount")}
                      onSort={handleSort}
                      isActive={sortState.column === "amount"}
                    />
                    <TableHeadCell className="bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300">Ref</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
                  {sortedExpenses.map((expense: Expense, index: number) => {
                    const isExpandable = shouldEnableExpansion(expense, index);
                    const isExpandedRow = isExpanded(expense.id);

                    return (
                      <React.Fragment key={expense.id}>
                        <TableRow
                          className={`transition-colors duration-150 ${
                            isExpandable
                              ? "cursor-pointer"
                              : ""
                          } ${
                            isExpandedRow
                              ? "bg-gray-50 dark:bg-gray-800/50"
                              : "bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/30"
                          }`}
                          onClick={() => handleRowClick(expense, index)}
                        >
                          <TableCell>
                            <span className="text-sm text-gray-900 dark:text-gray-100">{formatDate(expense.authorization_time)}</span>
                          </TableCell>
                          <TableCell className="text-ld">
                            <div className="flex items-center gap-3">
                              {expense.merchant_logo?.medium_url
                                ? (
                                  <img
                                    src={expense.merchant_logo.medium_url}
                                    alt={`Logo ${expense.merchant_name}`}
                                    className="size-8 rounded-md object-cover flex-shrink-0"
                                  />
                                )
                                : (
                                  <ExpenseDefaultLogo label={expense.merchant_name} />
                                )}
                              <span className="text-gray-900 dark:text-gray-100">{expense.merchant_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-ld">
                            {expense.amount < 0
                              ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-green-600 dark:text-green-400 font-medium">
                                    +
                                    {formatEuro(Math.abs(expense.amount))}
                                  </span>
                                  <Badge color="success" size="xs">Crédit</Badge>
                                </div>
                              )
                              : (
                                <span className="text-red-600 dark:text-red-400 font-medium">
                                  -
                                  {formatEuro(expense.amount)}
                                </span>
                              )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap max-w-[120px]">
                            <Tooltip content={expense.id} placement="top" className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-md">
                              <span className="text-sm text-gray-900 dark:text-gray-100 truncate block max-w-[120px]">{expense.id}</span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <ExpenseDetailsPanel
                          key={`${expense.id}-details`}
                          expense={expense}
                          isVisible={isExpandedRow}
                        />
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
    </div>
  );
};
